'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { NewsletterSubscriberStatusDto } from '@cineamo/api/models/newsletter/NewsletterSubscriber.types';

export type NewsletterTopicPreselection = {
    type: string;
    referenceId?: string;
    label: string;
};

export type SubscriberStatus = NewsletterSubscriberStatusDto;

type NewsletterContextType = {
    newsletterEnabled: boolean;
    subscriberStatus: SubscriberStatus | null;
    refreshSubscriberStatus: () => void;
    isOpen: boolean;
    /**
     * The topics the entry point asked to start from. Empty means "no specific topic" — the modal
     * then pre-selects nothing, which is what a bell click should do when it carries no topic.
     * The all-topics-on default belongs to the standalone /newsletter page, not here (CIN-13995).
     */
    preselectedTopics: NewsletterTopicPreselection[];
    openModal: (topics?: NewsletterTopicPreselection[]) => void;
    closeModal: () => void;
};

const NewsletterModalContext = createContext<NewsletterContextType>({
    newsletterEnabled: false,
    subscriberStatus: null,
    refreshSubscriberStatus: () => {},
    isOpen: false,
    preselectedTopics: [],
    openModal: () => {},
    closeModal: () => {}
});

export function useNewsletterModal() {
    return useContext(NewsletterModalContext);
}

type NewsletterModalProviderProps = {
    cinemaId?: number;
    children: React.ReactNode;
};

export default function NewsletterModalProvider({ cinemaId, children }: NewsletterModalProviderProps) {
    const [newsletterEnabled, setNewsletterEnabled] = useState(false);
    const [subscriberStatus, setSubscriberStatus] = useState<SubscriberStatus | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [preselectedTopics, setPreselectedTopics] = useState<NewsletterTopicPreselection[]>([]);

    useEffect(() => {
        if (!cinemaId) return;

        const controller = new AbortController();

        fetch(`/api/newsletter/status?cinemaId=${cinemaId}`, { signal: controller.signal })
            .then((r) => r.json())
            .then((data) => {
                if (data?.subscribed) {
                    setSubscriberStatus(data);
                }
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setSubscriberStatus(null);
                }
            });

        return () => controller.abort();
    }, [cinemaId]);

    useEffect(() => {
        if (!cinemaId) return;

        const controller = new AbortController();

        fetch(`/api/newsletter/enabled?cinemaId=${cinemaId}`, { signal: controller.signal })
            .then((r) => r.json())
            .then((data) => setNewsletterEnabled(data?.isEnabled ?? false))
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setNewsletterEnabled(false);
                }
            });

        return () => controller.abort();
    }, [cinemaId]);

    const refreshSubscriberStatus = useCallback(() => {
        if (!cinemaId) return;

        fetch(`/api/newsletter/status?cinemaId=${cinemaId}`)
            .then((r) => r.json())
            .then((data) => {
                setSubscriberStatus(data?.subscribed ? data : null);
            })
            .catch(() => {});
    }, [cinemaId]);

    const openModal = useCallback((topics?: NewsletterTopicPreselection[]) => {
        setPreselectedTopics(topics ?? []);
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        // Cleared so the next opener starts from its own topics rather than inheriting the previous
        // entry point's.
        setPreselectedTopics([]);
    }, []);

    return (
        <NewsletterModalContext.Provider
            value={{
                newsletterEnabled,
                subscriberStatus,
                refreshSubscriberStatus,
                isOpen,
                preselectedTopics,
                openModal,
                closeModal
            }}>
            {children}
        </NewsletterModalContext.Provider>
    );
}
